import { Button } from 'components/Button';
import { Grid, GridProps } from 'components/Grid/Grid';
import { ICONS, Icon } from 'components/Icon';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import {
  filterArrayEmpty,
  formatDateRange,
  numberToDate,
  rbAddReferencePrefix,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { doReportingGetReportListData } from 'store/actions';
import {
  selectRptReportItem,
  selectRptReportLoaded,
  selectRptReportOrder,
  selectRptReportTotalCount,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';
import { ReportingGridLoader } from '../../ReportingGridLoader';
import { Header } from './Header';
import { Visuals } from './Visuals';

type Report = ReportTypes[ReportEnum.checkschedule];
type DefinedGridProps = GridProps<
  Report['columns'],
  string,
  { reportKey: string }
>;

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const data = useSelector((state) =>
    selectRptReportItem(state, extraData.reportKey, id),
  ) as undefined | Report['structure'];

  return React.useMemo(() => {
    if (!data) {
      return null;
    }
    const icon = data.status.icon || ICONS.ExclamationSolid;
    return {
      onPress: () => {
        // console.log('Press');
      },
      columns: {
        check: (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <View style={{ flex: 1 }}>
              <Txt weight="bold" numberOfLines={1}>
                {data.check.name}
              </Txt>
              <View style={{ flexDirection: 'row' }}>
                {data.expired && (
                  <Icon
                    source={ICONS.ClockLight}
                    color={theme.colors.red}
                    size={16}
                    style={{
                      marginRight: theme.paddingHorizontal / 2,
                    }}
                  />
                )}
                <Txt
                  size="xsmall"
                  style={{
                    textDecorationLine: data.expired
                      ? 'line-through'
                      : undefined,
                    textDecorationColor: theme.colors.red,
                  }}
                  numberOfLines={1}
                >
                  {formatDateRange(
                    numberToDate(data.dateFrom),
                    numberToDate(data.dateTo),
                    'short',
                  )}
                </Txt>
              </View>
            </View>
          </View>
        ),
        status: (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <View>
              <Icon
                source={icon}
                style={{ marginRight: theme.paddingHorizontal }}
                color={`#${data.status.color}`}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Txt
                numberOfLines={1}
                weight="bold"
                style={{ color: `#${data.status.color}` }}
              >
                {data.status.name}
              </Txt>
              {(data.expired || data.attemptedCount !== 0) && (
                <Txt size="xsmall" numberOfLines={1}>
                  {[
                    data.expired ? 'Expired' : '',
                    data.attemptedCount !== 0
                      ? pluralise(
                          data.attemptedCount,
                          '{x} Attempt',
                          '{x} Attempts',
                        )
                      : '',
                  ]
                    .filter(filterArrayEmpty)
                    .join(' • ')}
                </Txt>
              )}
            </View>
          </View>
        ),
        element: (
          <View style={{ flex: 1 }}>
            <Txt numberOfLines={1} weight="bold">
              {data.element.typeSingular}
            </Txt>
            <Txt size="xsmall" numberOfLines={1}>
              {rbAddReferencePrefix(data.element.reference)}
              {data.element.customReference
                ? ` • ${data.element.customReference}`
                : ''}
            </Txt>
          </View>
        ),
        elementLocation: (
          <View style={{ flex: 1 }}>
            <Txt numberOfLines={3} lineHeightNone>
              {data.element.locationInfo.map((location, index) => (
                <React.Fragment key={index}>
                  {index > 0 ? ', ' : ''}
                  <Txt weight="bold" size="xsmall" lineHeightNone>
                    {location.label}:{' '}
                  </Txt>
                  <Txt size="xsmall" lineHeightNone>
                    {location.value}
                  </Txt>
                </React.Fragment>
              ))}
            </Txt>
          </View>
        ),
        asset: (
          <View style={{ flex: 1 }}>
            <Txt numberOfLines={1} weight="bold">
              {data.asset.name}
            </Txt>
            <Txt size="xsmall" numberOfLines={1}>
              {rbAddReferencePrefix(data.asset.reference)}
              {data.asset.customReference
                ? ` • ${data.asset.customReference}`
                : ''}
            </Txt>
          </View>
        ),
      },
    };
  }, [data]);
};

export const Main: React.FC<{ reportKey: string }> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Check',
      dataIndex: 'check',
      width: 200,
    },
    {
      name: 'Status',
      dataIndex: 'status',
      width: 200,
    },
    {
      name: 'Element',
      dataIndex: 'element',
      width: 200,
    },
    {
      name: 'Element Location',
      dataIndex: 'elementLocation',
      width: 200,
    },
    {
      name: 'Asset',
      dataIndex: 'asset',
      width: 200,
    },
  ]);

  const reportLoaded = useSelector((state) =>
    selectRptReportLoaded(state, reportKey),
  );
  const reportOrder = useSelector((state) =>
    selectRptReportOrder(state, reportKey),
  );
  const reportTotalCount = useSelector((state) =>
    selectRptReportTotalCount(state, reportKey),
  );
  const serverCount = reportTotalCount - reportOrder.length;

  const handleLoadReportData = React.useCallback(() => {
    dispatch(
      doReportingGetReportListData({
        key: reportKey,
        nextPage: true,
        closeModals: false,
        overlayLoader: false,
      }),
    );
  }, [dispatch, reportKey]);

  return (
    <Page
      isInModal={false}
      contentPaddingTop={0}
      ignoreSafeAreaBottom
      header={<Header reportKey={reportKey} />}
    >
      <Visuals reportKey={reportKey} />
      <View style={{ flex: 1 }}>
        <ReportingGridLoader reportKey={reportKey} />
        {!reportLoaded ? (
          <PageEmpty
            text="Waiting..."
            subtext="Press load to get started"
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : !reportOrder.length ? (
          <PageEmpty
            text="No Results Found"
            subtext="Try changing your filter"
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : (
          <Grid
            columns={columns}
            data={reportOrder}
            extraData={{ reportKey }}
            getRowData={useGetRowData}
            keyExtractor={(i) => `${i}`}
            ListFooterComponent={
              serverCount > 0 ? (
                <View
                  style={{
                    padding: theme.paddingHorizontal * 2,
                    paddingTop: theme.paddingHorizontal,
                    alignItems: 'flex-start',
                  }}
                >
                  <Button
                    text={pluralise(
                      serverCount,
                      '{x} More Result, Load',
                      '{x} More Results, Load More',
                    )}
                    onPress={handleLoadReportData}
                    icon={ICONS.ArrowDownLight}
                  />
                </View>
              ) : (
                <View style={{ height: theme.paddingHorizontal * 2 }} />
              )
            }
            // refreshControl,
          />
        )}
      </View>
    </Page>
  );
};
